import React, { Fragment, useContext, useEffect, useState } from "react";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import { HealthViewFiltersContext } from "../../../../pages/ExploreHealth";
import { HealthOverviewAndSummaryMutationContext } from "../../HealthView";
import { HealthOverviewDataProps } from "../../Common/HealthView.interfaces";
import HealthIntuneOverviewChart from "./HealthIntuneOverviewChart";
import HealthIntuneOverviewTable from "./HealthIntuneOverviewTable";

export const HealthIntuneOverviewContainer = (): React.JSX.Element => {
  const { data, isError, isLoading, viewMode } = useContext(HealthOverviewAndSummaryMutationContext);
  const filters: any = useContext(HealthViewFiltersContext);
  const [filteredItems, setFilteredItems] = useState<HealthOverviewDataProps[]>([]);

  // Change: chartFilters as array to handle multiple filters
  const [chartFilters, setChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  // Events
  const HandleAPIExcpetion = () => (isError && <DisplayAPIsException height={250} />) || "";

  const customProps = {
    healthViewFilters: JSON.stringify(filters)
  };

  // Handler to manage array of filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  // Hooks
  useEffect(() => {
    logEvent("OverviewTab", "Health View Page", "HealthIntuneOverviewContainer", customProps);
  }, [filters]);

  useEffect(() => {
    setFilteredItems(data?.healthDataList ?? []);

    if (isError) {
      logException(
        "Health View Page - Overview Tab",
        "HealthIntuneOverviewContainer",
        "getHealthDataSelector",
        isError,
        customProps
      );
    }
  }, [data, isError]);

  useEffect(() => {
    const filteredData = (data?.healthDataList || []).filter(
      // eslint-disable-next-line camelcase
      ({ intune_CustomerHealthScoreStatus, intune_SupportHealthScoreStatus }: any) => {
        // If no filters, show all data
        if (chartFilters.length === 0) {
          return true;
        }

        // Apply all filters - item must match ALL active filters
        return chartFilters.every((filter) => {
          if (filter.filterKey === "intune_CustomerHealthScoreStatus") {
            // eslint-disable-next-line camelcase
            return intune_CustomerHealthScoreStatus === filter.value;
          } else if (filter.filterKey === "intune_SupportHealthScoreStatus") {
            // eslint-disable-next-line camelcase
            return intune_SupportHealthScoreStatus === filter.value;
          }
          return true;
        });
      }
    );

    setFilteredItems(filteredData);
  }, [chartFilters, data]);

  return (
    <>
      <HealthIntuneOverviewChart
        loading={isLoading ?? false}
        onSelectedChartFilter={handleChartFilter}
        data={filteredItems}
        healthDataByCustomerHealthScoreStatusKustoLink={data?.healthDataByCustomerHealthScoreStatusKustoLink ?? ""}
        healthDataBySupportHealthScoreStatusKustoLink={data?.healthDataBySupportHealthScoreStatusKustoLink ?? ""}
        viewMode={viewMode}
      />
      {isLoading ? (
        <CustomShimmer columnCount={8} />
      ) : (
        (!isError && (
          <div>
            <HealthIntuneOverviewTable tableData={filteredItems} kustoLink={data?.healthDataKustoLink} viewMode={viewMode} />
          </div>
        )) ||
        ""
      )}
      <HandleAPIExcpetion />
    </>
  );
};

export const tenantCountMetricsTooltipMessage = (
  <Fragment>
    See number of tenants are trending over time for security products. The graph below shows 6 month trend.
    <br />
    <b>
      <i>Note: Filters are not supported for this graph.</i>
    </b>
  </Fragment>
);